/**
 * Feature E02-F05 — Kick-off questionnaire typed api-client + Query hooks
 * (T02 load / T06 save).
 *
 * Feature-owned (NOT in the shared queryKeys.ts / queries.ts). Uses the shared
 * `apiClient` (auto-injects the session Bearer token, normalizes the response,
 * never throws) + `unwrap` (the single throw-at-the-Query-boundary helper).
 * Contracts come from `@eer/shared` so the SPA speaks the exact same shapes as
 * the Fastify API.
 *
 * 1:1 port of `../EER app/public/assets/js/project-kickoff.js`:
 *   GET  /kickoff/questions            ← loadKickoffQuestionsTemplate (T02)
 *   PATCH /projects/:id (updates.*)    ← saveKickoffData / completeMeeting /
 *                                         _doAutoSaveKickoff  (T06)
 *
 * The PATCH `updates` bag merges into `projects.data` server-side (E02-F03
 * `update_project`) — it never overwrites the whole `data` (T06 acceptance #1).
 * After a successful save the caller refetches the project (source of truth is
 * the DB — T06 acceptance #3).
 */
import {
  useQuery,
  useMutation,
  useQueryClient,
  type QueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import {
  kickoffQuestionsListResultSchema,
  mergedKickoffQuestionsResultSchema,
  salarySearchResponseSchema,
  kickoffEnrichmentResponseSchema,
  prefillQuestionnaireResponseSchema,
  companyWebsiteFactsResponseSchema,
  type CompanyWebsiteFactsResponse,
  type KickoffQuestionItem,
  type ProjectDetailCard,
  type SalarySearchParams,
  type SalarySearchResponse,
  type KickoffEnrichmentResponse,
  type PrefillQuestionnaireResponse,
  type PrefillMode,
} from '@eer/shared';
import { apiClient, unwrap, type ApiResultError } from '../api/client.js';
import { qk } from '../api/queryKeys.js';
import type { KickoffSavePatch } from './kickoffModel.js';

/** Feature-local query key for the cached base template (kept out of the registry). */
export const KICKOFF_TEMPLATE_QUERY_KEY = ['kickoff', 'questions', 'template'] as const;

/** Per-project key so a refetch after save re-pulls the saved answers (DB = truth). */
export function kickoffProjectQueryKey(projectId: number): readonly unknown[] {
  return ['kickoff', 'project', projectId] as const;
}

/**
 * Load the active base questionnaire template (GET /kickoff/questions), mapped to
 * the front-end template items `{ category, text, answer:'', done:false }` the
 * merge consumes (`loadKickoffQuestionsTemplate`). On failure the caller shows
 * "Questionnaire template not loaded — contact admin" and falls back to [].
 */
export async function fetchKickoffTemplate(): Promise<KickoffQuestionItem[]> {
  const result = await apiClient.get<unknown>('/kickoff/questions', {
    command: 'manage_kickoff_questions',
  });
  const body = kickoffQuestionsListResultSchema.parse(unwrap(result));
  return body.questions.map((q) => ({
    category: q.category,
    text: q.text,
    answer: '',
    done: false,
  }));
}

/** Persist kickoff answers/notes/transcript (and optionally meeting_completed). */
export async function saveKickoffUpdates(args: {
  projectId: number;
  updates: KickoffSavePatch;
  /** E02-F05-T09 — let the PATCH outlive the page (pagehide flush). */
  keepalive?: boolean;
}): Promise<void> {
  const result = await apiClient.patch<unknown>(
    `/projects/${args.projectId}`,
    { updates: args.updates },
    { command: 'update_project', ...(args.keepalive ? { keepalive: true } : {}) },
  );
  // The route returns the full project detail; we only need success (no throw).
  unwrap(result);
}

/**
 * E02-F05-T09 — mirror a kick-off save into the shared project-detail cache
 * (`qk.projects.detail`), which is where the Kick-off step reads its saved
 * notes/transcript on mount. Without this the cache kept the text from the
 * first load, so returning to the step (e.g. from Generation) showed stale or
 * empty notes. Optimistic: applied when the save is SENT, so a fast return to
 * the step already sees the latest text.
 */
export function patchKickoffDetailCache(
  queryClient: QueryClient,
  projectId: number,
  updates: KickoffSavePatch,
): void {
  queryClient.setQueryData<ProjectDetailCard>(qk.projects.detail(projectId), (old) => {
    if (!old) return old;
    const data: Record<string, unknown> = { ...(old.project.data ?? {}) };
    if (updates.kickoff_notes !== undefined) {
      data.kickoff_notes = updates.kickoff_notes;
      delete data.kickoffNotes;
    }
    if (updates.kickoff_transcript !== undefined) {
      data.kickoff_transcript = updates.kickoff_transcript;
      delete data.kickoffTranscript;
    }
    data.kickoff_questions = updates.kickoff_questions;
    if (updates.meeting_completed !== undefined) data.meeting_completed = updates.meeting_completed;
    return { ...old, project: { ...old.project, data } };
  });
}

/**
 * E02-F05-T09 — fire-and-forget save that does NOT depend on a mounted
 * component (unmount / pagehide flush of a pending auto-save). Patches the
 * detail cache first; on failure re-pulls the detail so the cache is truthful.
 */
export function useFlushKickoffSave(
  projectId: number,
): (updates: KickoffSavePatch, opts?: { keepalive?: boolean }) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (updates, opts) => {
      patchKickoffDetailCache(queryClient, projectId, updates);
      saveKickoffUpdates({ projectId, updates, keepalive: opts?.keepalive }).catch(() => {
        void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
      });
    },
    [queryClient, projectId],
  );
}

/**
 * Fetch the merged kickoff questionnaire for a project (GET /projects/:id/kickoff-questions).
 * The server merges the active base template + position-type extras + the project's
 * saved answers (`_buildMergedKickoffQuestions`). This is the source of truth for
 * the KickoffView's `initialQuestions`.
 */
export async function fetchProjectKickoffQuestions(
  projectId: number,
): Promise<KickoffQuestionItem[]> {
  const result = await apiClient.get<unknown>(`/projects/${projectId}/kickoff-questions`, {
    command: 'get_1_project',
  });
  const body = mergedKickoffQuestionsResultSchema.parse(unwrap(result));
  return body.questions;
}

/**
 * Per-project merged questionnaire query (GET /projects/:id/kickoff-questions).
 * Keyed by `kickoffProjectQueryKey` so a save mutation invalidates it and the
 * view re-hydrates with fresh DB answers.
 */
export function useProjectKickoffQuestions(
  projectId: number,
): UseQueryResult<KickoffQuestionItem[], ApiResultError> {
  return useQuery<KickoffQuestionItem[], ApiResultError>({
    queryKey: kickoffProjectQueryKey(projectId),
    queryFn: () => fetchProjectKickoffQuestions(projectId),
    enabled: projectId > 0,
  });
}

/**
 * Template query — fetched once and cached app-wide (the base reference list is
 * global). On error the consumer treats `data ?? []` as an empty template and
 * surfaces the admin-contact toast.
 */
export function useKickoffTemplate(): UseQueryResult<KickoffQuestionItem[], ApiResultError> {
  return useQuery<KickoffQuestionItem[], ApiResultError>({
    queryKey: KICKOFF_TEMPLATE_QUERY_KEY,
    queryFn: fetchKickoffTemplate,
    staleTime: 5 * 60 * 1000, // reference data — cache for 5 min
  });
}

/**
 * Save mutation — invalidates the per-project key so the questionnaire refetches
 * the merged answers from the DB after a write (T06 acceptance #3).
 */
export function useSaveKickoff(
  projectId: number,
): UseMutationResult<void, ApiResultError, KickoffSavePatch> {
  const queryClient = useQueryClient();
  return useMutation<void, ApiResultError, KickoffSavePatch>({
    mutationFn: (updates) => saveKickoffUpdates({ projectId, updates }),
    // E02-F05-T09 — keep the detail cache (the step's mount source) in step.
    onMutate: (updates) => patchKickoffDetailCache(queryClient, projectId, updates),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kickoffProjectQueryKey(projectId) });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// T03 — market salary search (POST /projects/:id/ai/search_salary_rate)
// ---------------------------------------------------------------------------

/**
 * Search the market salary rate for the "Market salary rate" question
 * (`searchSalaryRate`). POSTs the collected context (role/location/experience/
 * industry — gathered from the questionnaire answers by keyword) to the salary AI
 * sub-flow and returns its union response. The SPA reads `res.salary_rate` on
 * success and writes it into the salary question's answer; the failure branch
 * (`{ success:false, error }`) surfaces as a toast. Both are HTTP 200 (legacy
 * parity), so the union is parsed straight off the unwrapped body.
 */
export async function searchSalaryRate(args: {
  projectId: number;
  searchParams: SalarySearchParams;
}): Promise<SalarySearchResponse> {
  const result = await apiClient.post<unknown>(
    `/projects/${args.projectId}/ai/search_salary_rate`,
    { searchParams: args.searchParams },
    { command: 'ai_request' },
  );
  return salarySearchResponseSchema.parse(unwrap(result));
}

/** Variables for the salary-search mutation (which question + the search context). */
export interface SalarySearchVars {
  readonly questionIdx: number;
  readonly searchParams: SalarySearchParams;
}

/**
 * Salary-search mutation (T03 — the per-question market-rate button). Keyed by
 * project; the caller writes `res.salary_rate` into the question answer on success
 * and re-saves the questionnaire (so the found rate persists like a typed answer).
 */
export function useSearchSalaryRate(
  projectId: number,
): UseMutationResult<SalarySearchResponse, ApiResultError, SalarySearchVars> {
  return useMutation<SalarySearchResponse, ApiResultError, SalarySearchVars>({
    mutationFn: ({ searchParams }) => searchSalaryRate({ projectId, searchParams }),
  });
}

// ---------------------------------------------------------------------------
// Kickoff AI actions (POST /projects/:id/ai/{prefill_questionnaire |
// kickoff_enrichment}) — E03-F03 group.
//
// 1:1 port of the SPA handlers in `../EER app/public/assets/js/project-kickoff.js`:
//   prefillQuestionnaire ~604 (also reused by processTranscript ~581),
//   runKickoffAi ~787.
// Both POST an optional body; the actor + projectId are server-side (IDOR).
// Each is a distinct HTTP 200 union the SPA branches on (success / {success:false}).
//
// PK-QA-16 — the "search company website" AI action was removed: its route was
// deregistered (near-always a no-op + real per-click Anthropic web-search cost).
// The KickoffView button is now a pure client-side Google `site:`-lookup gated on
// the human-entered `Client.website`; no mutation lives here anymore. The dormant
// backend service is kept for a future third-party-search-API pipeline (see ADR).
// ---------------------------------------------------------------------------

/**
 * AI pre-fill the questionnaire from all available project data (emails, files,
 * context, and — when present — the transcript). The service persists the merged
 * questionnaire itself, so the caller refetches the project after
 * (`prefillQuestionnaire`). The response carries extracted_company/contact for the
 * project header auto-fill.
 */
export async function prefillQuestionnaire(
  projectId: number,
  mode?: PrefillMode,
): Promise<PrefillQuestionnaireResponse> {
  const result = await apiClient.post<unknown>(
    `/projects/${projectId}/ai/prefill_questionnaire`,
    mode ? { mode } : {},
    { command: 'ai_request' },
  );
  return prefillQuestionnaireResponseSchema.parse(unwrap(result));
}

/**
 * Run the post-completion kickoff enrichment over the saved project data
 * (`runKickoffAi`, after saveKickoffData). Returns the structured enrichment or a
 * `{ success:false, error }` failure body (both HTTP 200, legacy parity).
 */
export async function runKickoffEnrichment(projectId: number): Promise<KickoffEnrichmentResponse> {
  const result = await apiClient.post<unknown>(
    `/projects/${projectId}/ai/kickoff_enrichment`,
    {},
    { command: 'ai_request' },
  );
  return kickoffEnrichmentResponseSchema.parse(unwrap(result));
}

/**
 * B3/KS-10 — "AI Processing", the POST-call action, as ONE operation.
 *
 * It runs two AI passes back to back because they answer two different questions
 * about the same meeting: `kickoff_enrichment` writes the structured Kickoff Data
 * panel, and `prefill_questionnaire` in `post_meeting` mode turns the notes and
 * the transcript into questionnaire answers. Before this, only the first ran —
 * so a recruiter who typed her notes and completed the meeting had NO path left
 * to get them into the answers (Ksenia's project 168: 937 chars of notes, 0 of 32
 * answered).
 *
 * Sequential, not parallel, and the enrichment failure short-circuits: the second
 * pass reads what the first persisted. Both errors propagate — a swallowed second
 * step would look exactly like the bug being fixed here.
 *
 * Cost note: one click = two LLM calls (three when a transcript is present and
 * the v2 pass runs). That is a deliberate, PM-approved trade: it is still ONE
 * explicit human action, which is the invariant that matters (no automatic AI).
 */
export async function runKickoffAiProcessing(
  projectId: number,
): Promise<KickoffEnrichmentResponse> {
  const enrichment = await runKickoffEnrichment(projectId);
  await prefillQuestionnaire(projectId, 'post_meeting');
  return enrichment;
}

/**
 * Pre-fill mutation (AI Pre-fill Questionnaire + Process Transcript). On success
 * the questionnaire has been persisted server-side; invalidate the per-project key
 * so the view re-hydrates with the merged DB answers.
 */
export function usePrefillQuestionnaire(
  projectId: number,
): UseMutationResult<PrefillQuestionnaireResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<PrefillQuestionnaireResponse, ApiResultError, void>({
    mutationFn: () => prefillQuestionnaire(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kickoffProjectQueryKey(projectId) });
    },
  });
}

// ---------------------------------------------------------------------------
// E03-F02-T11 — the REAL «Search Company Website»
// (POST /projects/:id/ai/company_website_facts)
// ---------------------------------------------------------------------------

/**
 * Collect company facts from the client's OWN website. The api downloads the site
 * itself (free, cache-first scrape — the same primitive the Research chain uses)
 * and extracts the profile with Haiku; `web_search` is never called, so this is
 * nothing like the deregistered `search_website_for_missing` route it replaces.
 *
 * The service persists the result to `projects.data.company_facts`, so the caller
 * only has to invalidate the project-detail key and the panel re-hydrates.
 */
export async function searchCompanyWebsiteFacts(
  projectId: number,
): Promise<CompanyWebsiteFactsResponse> {
  const result = await apiClient.post<unknown>(
    `/projects/${projectId}/ai/company_website_facts`,
    {},
    { command: 'ai_request' },
  );
  return companyWebsiteFactsResponseSchema.parse(unwrap(result));
}

/**
 * Company-facts mutation (the Kick-off "Search Company Website" button). One
 * explicit click = one run (no-auto-AI policy: no retry, no background refresh).
 * Invalidates the shared project-detail key — that cache is where `KickoffView`
 * reads `projects.data.company_facts` from.
 */
export function useCompanyWebsiteFacts(
  projectId: number,
): UseMutationResult<CompanyWebsiteFactsResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<CompanyWebsiteFactsResponse, ApiResultError, void>({
    mutationFn: () => searchCompanyWebsiteFacts(projectId),
    retry: false,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: qk.projects.detail(projectId) });
    },
  });
}

/**
 * Post-completion mutation (AI Processing / runKickoffAi). Invalidates the
 * per-project key so both the enrichment panel AND the questionnaire re-pull the
 * DB state — B3/KS-10 made this action write answers too, and a stale list would
 * look exactly like "ничего не распозналось".
 */
export function useKickoffEnrichment(
  projectId: number,
): UseMutationResult<KickoffEnrichmentResponse, ApiResultError, void> {
  const queryClient = useQueryClient();
  return useMutation<KickoffEnrichmentResponse, ApiResultError, void>({
    mutationFn: () => runKickoffAiProcessing(projectId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: kickoffProjectQueryKey(projectId) });
    },
  });
}
